import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { AlertTriangle, CheckCircle, Info, ChevronLeft, ChevronRight, ClipboardList, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";

const SEV_TONES = {
  critical: "bg-red-50 text-red-700 border border-red-200",
  high: "bg-orange-50 text-orange-700 border border-orange-200",
  medium: "bg-amber-50 text-amber-700 border border-amber-200",
  warning: "bg-amber-50 text-amber-700 border border-amber-200",
  low: "bg-slate-50 text-slate-700 border border-slate-200",
  info: "bg-sky-50 text-sky-700 border border-sky-200",
};
const PAGE_SIZE = 20;

export default function AlertManagement() {
  const { alerts, setAlerts, sites } = useSiteContext();
  const { can } = usePermissions();
  const nav = useNavigate();
  const [sevSet, setSevSet] = useState(new Set());
  const [siteFilter, setSiteFilter] = useState("all");
  const [statusTab, setStatusTab] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [page, setPage] = useState(1);

  const ack = async (id) => {
    try {
      await api.post(`/alerts/${id}/acknowledge`);
      setAlerts((prev) => prev.map((a) => a.alert_id === id ? { ...a, acknowledged: true, status: "acknowledged" } : a));
      toast.success("Alert acknowledged");
    } catch { toast.error("Failed"); }
  };
  const resolve = async (id) => {
    try {
      await api.post(`/alerts/${id}/resolve`);
      setAlerts((prev) => prev.map((a) => a.alert_id === id ? { ...a, acknowledged: true, status: "resolved" } : a));
      toast.success("Alert resolved");
    } catch { toast.error("Failed"); }
  };

  const toggleSev = (s) => {
    const next = new Set(sevSet);
    next.has(s) ? next.delete(s) : next.add(s);
    setSevSet(next); setPage(1);
  };

  const filtered = useMemo(() => {
    return alerts.filter((a) => {
      const status = a.status || (a.acknowledged ? "acknowledged" : "open");
      if (statusTab !== "all" && status !== statusTab) return false;
      if (sevSet.size && !sevSet.has(a.severity)) return false;
      if (siteFilter !== "all" && a.site_id !== siteFilter) return false;
      if (start && new Date(a.created_at) < new Date(start)) return false;
      if (end && new Date(a.created_at) > new Date(end + "T23:59:59")) return false;
      return true;
    });
  }, [alerts, sevSet, siteFilter, statusTab, start, end]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const ackAll = async () => {
    const open = filtered.filter((a) => (a.status || (a.acknowledged ? "acknowledged" : "open")) === "open");
    if (!open.length) { toast.message("No open alerts"); return; }
    if (!window.confirm(`Acknowledge all ${open.length} open alerts? This will log all acknowledgements under your name.`)) return;
    try {
      await Promise.all(open.map((a) => api.post(`/alerts/${a.alert_id}/acknowledge`)));
      setAlerts((prev) => prev.map((a) => open.some((o) => o.alert_id === a.alert_id) ? { ...a, acknowledged: true, status: "acknowledged" } : a));
      toast.success(`${open.length} alerts acknowledged`);
    } catch { toast.error("Bulk acknowledge failed"); }
  };

  const counts = useMemo(() => {
    const o = { all: alerts.length, open: 0, acknowledged: 0, resolved: 0 };
    alerts.forEach((a) => {
      const s = a.status || (a.acknowledged ? "acknowledged" : "open");
      o[s] = (o[s] || 0) + 1;
    });
    return o;
  }, [alerts]);

  const Icon = ({ sev }) => sev === "critical" || sev === "high" ? <AlertTriangle className="w-4 h-4 text-red-400" strokeWidth={2.5} /> : sev === "medium" || sev === "warning" ? <AlertTriangle className="w-4 h-4 text-amber-400" strokeWidth={2.5} /> : <Info className="w-4 h-4 text-sky-400" strokeWidth={2.5} />;

  return (
    <div className="space-y-5" data-testid="alerts-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-gray-500">Dashboard → Alerts</div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Alert Management</h1>
          <p className="text-sm text-gray-500 mt-1">{filtered.length} of {alerts.length} alerts · auto-refresh 8s</p>
        </div>
        <button onClick={() => nav("/alerts/settings")} className="text-xs text-orange-700 hover:text-orange-900 px-3 py-1.5 rounded-lg bg-orange-50 hover:bg-orange-100 border border-orange-200 inline-flex items-center gap-1.5" data-testid="alerts-settings-link">
          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} /> Alert Settings
        </button>
      </div>

      <div className="glass p-4 rounded-lg space-y-3">
        <div className="flex flex-wrap gap-2">
          {["critical", "high", "medium", "low"].map((s) => (
            <button
              key={s} onClick={() => toggleSev(s)}
              data-testid={`sev-chip-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border transition ${sevSet.has(s) ? SEV_TONES[s] + " ring-2 ring-offset-2 ring-offset-white ring-orange-500" : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"}`}
            >
              {s}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Select value={siteFilter} onValueChange={(v) => { setSiteFilter(v); setPage(1); }}>
            <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="alert-site-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-gray-200 text-gray-900">
              <SelectItem value="all">All Sites</SelectItem>
              {sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPage(1); }} className="bg-gray-50 border-gray-200" placeholder="Start date" />
          <Input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPage(1); }} className="bg-gray-50 border-gray-200" placeholder="End date" />
        </div>

        <Tabs value={statusTab} onValueChange={(v) => { setStatusTab(v); setPage(1); }}>
          <TabsList className="bg-gray-50 border border-gray-200">
            <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
            <TabsTrigger value="open">Open ({counts.open || 0})</TabsTrigger>
            <TabsTrigger value="acknowledged">Acked ({counts.acknowledged || 0})</TabsTrigger>
            <TabsTrigger value="resolved">Resolved ({counts.resolved || 0})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Card list — works on both desktop and mobile */}
      <div className="space-y-3" data-testid="alerts-list">
        {statusTab === "open" && pageRows.length > 0 && can("alert_management", "update") && (
          <Button onClick={ackAll} className="bg-amber-500/20 hover:bg-amber-50 border border-amber-200 text-amber-700" data-testid="ack-all-btn">
            ✓ Mark All as Acknowledged ({filtered.filter((a) => (a.status || (a.acknowledged ? "acknowledged" : "open")) === "open").length})
          </Button>
        )}
        {pageRows.map((a) => {
          const status = a.status || (a.acknowledged ? "acknowledged" : "open");
          const sevConfig = {
            critical: { border: "border-l-red-600", badge: "bg-red-50 text-red-700 border-red-200" },
            high: { border: "border-l-orange-600", badge: "bg-orange-50 text-orange-700 border-orange-200" },
            medium: { border: "border-l-amber-600", badge: "bg-amber-50 text-amber-700 border-amber-200" },
            warning: { border: "border-l-amber-600", badge: "bg-amber-50 text-amber-700 border-amber-200" },
            low: { border: "border-l-blue-600", badge: "bg-blue-50 text-blue-700 border-blue-200" },
            info: { border: "border-l-sky-600", badge: "bg-sky-50 text-sky-700 border-sky-200" },
          }[a.severity] || { border: "border-l-slate-600", badge: "bg-slate-50 text-slate-700 border-slate-200" };
          const statusBadge = status === "open" ? "bg-red-50 text-red-700" : status === "acknowledged" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 border border-emerald-200 text-emerald-700";
          return (
            <div key={a.alert_id} data-testid={`alert-row-${a.alert_id}`}
              className={`glass rounded-lg border-l-4 ${sevConfig.border} grid grid-cols-1 md:grid-cols-[120px,1fr,220px] gap-3 p-4`}>
              {/* Severity column */}
              <div className="flex md:flex-col items-start md:items-center gap-2">
                <Icon sev={a.severity} />
                <Badge className={`${sevConfig.badge} border font-bold uppercase text-[10px] tracking-wider px-2 py-0.5`}>{a.severity}</Badge>
              </div>
              {/* Body */}
              <div className="min-w-0">
                <div className="font-bold text-gray-900 text-base leading-snug">{a.title}</div>
                {a.message && <div className="text-sm text-gray-500 mt-0.5">{a.message}</div>}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-gray-500">
                  <span className="inline-flex items-center gap-1">📍 {sites.find((s) => s.site_id === a.site_id)?.name || a.site_id}</span>
                  <span className="inline-flex items-center gap-1">🕐 {new Date(a.created_at).toLocaleString()}</span>
                </div>
              </div>
              {/* Right column — status + actions */}
              <div className="flex md:flex-col items-stretch md:items-end gap-2 md:justify-center">
                <Badge className={`${statusBadge} border-0 text-[10px] uppercase tracking-wider font-bold`}>{status}</Badge>
                <div className="flex gap-2 md:flex-col w-full md:w-auto">
                  {status === "open" && can("alert_management", "update") && <Button size="sm" onClick={() => ack(a.alert_id)} className="flex-1 md:flex-none bg-amber-100 hover:bg-amber-200 text-amber-700 border border-amber-200 h-8 font-semibold" data-testid={`ack-${a.alert_id}`}>✓ Ack</Button>}
                  {status !== "resolved" && can("alert_management", "update") && <Button size="sm" onClick={() => resolve(a.alert_id)} className="flex-1 md:flex-none bg-emerald-100 hover:bg-emerald-200 text-emerald-700 border border-emerald-200 h-8 font-semibold" data-testid={`resolve-${a.alert_id}`}>✓✓ Resolve</Button>}
                  {status === "resolved" && <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" strokeWidth={2.5} /> Resolved</span>}
                  <Button size="sm" onClick={() => {
                    const sysMap = { hydrant: "Fire-Hydrant", sprinkler: "Sprinkler", tank: "Plumbing", detection: "Other", ventilation: "Ventilation" };
                    const prioMap = { critical: "Critical", high: "High", warning: "Medium", medium: "Medium", info: "Low", low: "Low" };
                    const params = new URLSearchParams({
                      title: a.title || "Alert",
                      desc: a.message || a.title || "",
                      system: sysMap[a.category] || "Other",
                      priority: prioMap[a.severity] || "Medium",
                    });
                    nav(`/work-orders?${params.toString()}`);
                  }} className="flex-1 md:flex-none bg-orange-100 hover:bg-orange-200 text-orange-700 border border-orange-200 h-8 font-semibold" data-testid={`raise-wo-${a.alert_id}`}>
                    <ClipboardList className="w-3 h-3 mr-1" strokeWidth={2.5} /> Raise WO
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        {!pageRows.length && (
          <div className="glass rounded-lg p-10 text-center text-gray-500"><CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-500" strokeWidth={2.5} />No alerts match the current filters.</div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="alerts-prev"><ChevronLeft className="w-4 h-4 mr-1" strokeWidth={2.5} />Prev</Button>
          <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
          <Button variant="ghost" size="sm" disabled={page === totalPages} onClick={() => setPage(page + 1)} data-testid="alerts-next">Next<ChevronRight className="w-4 h-4 ml-1" strokeWidth={2.5} /></Button>
        </div>
      )}
    </div>
  );
}
